import * as React from 'react';
import { useState, useReducer } from 'react';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@material-ui/data-grid';
import { Button, IconButton, Tooltip, CircularProgress } from '@material-ui/core';
import { CSVLink } from 'react-csv';
import { green } from '@material-ui/core/colors';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { useParams } from 'react-router';
import { produce } from 'immer';
import useFetchDdpcrByPlateId from '../../Hooks/qlp_ddpcr/useFetchDdpcrByPlateId';
import useFetchDdpcrRunInfo from '../../Hooks/qlp_ddpcr/useFetchDdpcrRunInfo';
import useFetchDdpcrFileDownload from '../../Hooks/qlp_ddpcr/useFetchDdpcrFileDownload';
import RightSide from './RightSide';
import { graphReducer } from '../../../reducers/graphReducer';
import '../../common/styles/rightSide.css';

const curriedGraphReducer = produce(graphReducer);

class GraphErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <p style={{ color: '#e57373', padding: '1rem' }}>
          Graph data unavailable — ingestion may still be in progress. Re-drive to regenerate.
        </p>
      );
    }
    return this.props.children;
  }
}

const initialState = {
  plateId: null,
  wellId: null,
};

const RUN_INFO_LABELS = {
  make: 'Make',
  model: 'Model',
  serialnumber: 'Unit',
  instrument: 'Instrument',
  plate_name: 'Plate Name',
  plate_size: 'Plate Size',
  plate_rows: 'Rows',
  plate_columns: 'Columns',
  run_uuid: 'Run UUID',
  fileinfo_datetime: 'Run Date',
};

// Columns rendered to 3 decimal places incl. trailing zeros (e.g. 0.850, 0.000).
const DROPLET_VOL_COLS = new Set(['Avg Droplet Vol']);

// forwardRef wrapper so CSVLink can be used as MUI Button's `component` — avoids the
// MUI ref warning and a per-render remount of the underlying anchor.
const CsvExportLink = React.forwardRef((props, _ref) => <CSVLink {...props} />);
CsvExportLink.displayName = 'CsvExportLink';

// Custom DataGrid toolbar: keeps the standard column/filter/density controls and
// replaces the built-in export with a selection-aware CSV export. When wells are
// selected, only those rows export; when nothing is selected, the whole table does.
const SelectionExportToolbar = ({ exportData, exportHeaders, exportFilename }) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <Button
      component={CsvExportLink}
      data={exportData}
      headers={exportHeaders}
      filename={exportFilename}
      size="small"
      color="primary"
    >
      Export
    </Button>
  </GridToolbarContainer>
);

const RunInfoBar = ({ info }) => (
  <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] py-3 border-b border-gray-200">
    {Object.entries(RUN_INFO_LABELS).map(([key, label]) =>
      info[key] != null ? (
        <span key={key}>
          <span className="font-semibold text-custom-green">{label}: </span>
          <span>{String(info[key])}</span>
        </span>
      ) : null
    )}
  </div>
);

const Details = () => {
  const { plateUuid, filename } = useParams();

  const [state, dispatch] = useReducer(curriedGraphReducer, initialState);
  const [splitView, setSplitView] = useState(false);
  const [isDrag, setIsDrag] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);

  const ddpcrDetailQuery = useFetchDdpcrByPlateId(plateUuid);
  const runInfoQuery = useFetchDdpcrRunInfo(plateUuid);
  const fileDownloadQuery = useFetchDdpcrFileDownload(plateUuid);

  const handleDownload = async () => {
    const result = await fileDownloadQuery.refetch();
    const signedUrl = result?.data?.signedUrl;
    if (signedUrl) {
      const a = document.createElement('a');
      a.href = signedUrl;
      a.download = result.data.data_filename || 'ddpcr_file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const data = ddpcrDetailQuery?.isSuccess ? ddpcrDetailQuery?.data?.data : null;
  const runInfo = runInfoQuery?.isSuccess ? runInfoQuery?.data?.data?.[0] : null;

  // Build rows + columns from flat columnar S3 payload
  let columnNames = data
    ? data.columns.filter(col => col !== 'index' && col !== 'columns' && data.columns.includes(col))
    : [];
  let rows = [];
  let columns = [];

  if (columnNames.length > 0) {
    const rowCount = Object.keys(data[columnNames[0]]).length;

    rows = Array.from({ length: rowCount }, (_, id) => ({
      id,
      ...columnNames.reduce((obj, col) => {
        const value = data[col][id];
        if (typeof value === 'number') {
          // Keep droplet-volume columns at full (3dp) precision so the column's
          // valueFormatter can show trailing zeros; round everything else to 2dp.
          obj[col] = DROPLET_VOL_COLS.has(col) ? value : parseFloat(value.toFixed(2));
        } else {
          obj[col] = value;
        }
        return obj;
      }, {}),
    }));

    columns = columnNames.map((name, id) => {
      const base = {
        field: name,
        headerName: name,
        width: Math.max(150, (name?.length ?? 0) * 10 + 72),
        id,
      };
      if (DROPLET_VOL_COLS.has(name)) {
        base.valueFormatter = ({ value }) =>
          value === null || value === undefined || value === '' ? value : Number(value).toFixed(3);
      }
      if (name === 'Well') {
        return {
          ...base,
          renderCell: params => (
            <Button
              size="small"
              style={{ color: green[600], textTransform: 'none', fontWeight: 700, padding: 0 }}
              onClick={e => {
                e.stopPropagation();
                dispatch({ type: '2d_graph', plateId: plateUuid, wellId: params.row.Well });
                setSplitView(true);
              }}
            >
              {params.row.Well}
            </Button>
          ),
        };
      }
      return base;
    });
  }

  function resetColumnSizes() {
    const page = document.getElementById('details-page');
    if (page) page.style.gridTemplateColumns = '6fr 2px 4fr';
  }

  function endDrag() {
    setIsDrag(false);
    setCursor('auto');
  }

  function onDrag(event) {
    if (isDrag) {
      const page = document.getElementById('details-page');
      const leftCol = document.getElementById('details-leftcol');
      const leftColWidth = isDrag ? event.clientX : leftCol.clientWidth;
      const cols = [leftColWidth, 2, page.clientWidth - 4 - leftColWidth];
      page.style.gridTemplateColumns = cols.map(c => c + 'px').join(' ');
      event.preventDefault();
    }
  }

  function setCursor(cursor) {
    const page = document.getElementById('details-page');
    if (page) page.style.cursor = cursor;
  }

  function startLeftDrag() {
    setIsDrag(true);
    setCursor('ew-resize');
  }

  const header = (
    <div className="flex flex-col pb-2">
      <div className="flex items-center justify-between pt-4 pb-1">
        {filename && (
          <span className="font-bold text-custom-green text-[13px]">File Name: {decodeURIComponent(filename)}</span>
        )}
        <Tooltip title="Download ddPCR file">
          <span>
            <IconButton
              size="small"
              onClick={handleDownload}
              disabled={fileDownloadQuery.isFetching}
              style={{ color: green[600] }}
            >
              {fileDownloadQuery.isFetching ? (
                <CircularProgress size={18} style={{ color: green[600] }} />
              ) : (
                <CloudDownloadIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </div>
      {runInfo && <RunInfoBar info={runInfo} />}
    </div>
  );

  // Export honors selection: selected rows when any are checked, otherwise the
  // whole table. Strip the synthetic `id` field from the exported records.
  const exportData = (selectionModel.length ? rows.filter(row => selectionModel.includes(row.id)) : rows).map(
    ({ id: _id, ...rest }) => rest
  );
  const exportHeaders = columnNames.map(col => ({ label: col, key: col }));
  const exportFilename = `${filename ? decodeURIComponent(filename) : 'ddpcr'}_export.csv`;

  const grid = (
    <DataGrid
      components={{ Toolbar: SelectionExportToolbar }}
      componentsProps={{ toolbar: { exportData, exportHeaders, exportFilename } }}
      rows={rows}
      columns={columns}
      pageSize={100}
      rowsPerPageOptions={[100]}
      checkboxSelection
      disableSelectionOnClick
      selectionModel={selectionModel}
      onSelectionModelChange={model => setSelectionModel(model)}
    />
  );

  if (splitView && state.plateId) {
    return (
      <div className="oneToolContainer flex flex-col" style={{ height: 'calc(100dvh - 112px)' }}>
        {header}
        <span onResize={resetColumnSizes}>
          <Button style={{ color: green[500] }} className="toggle-btn" onClick={() => setSplitView(false)}>
            Table
          </Button>
          <div
            className="mainContainer"
            id="details-page"
            onMouseUp={endDrag}
            onMouseMove={onDrag}
            style={{ height: 'calc(100dvh - 192px)' }}
          >
            <aside id="details-leftcol">{grid}</aside>
            <div id="leftDrag" onMouseDown={startLeftDrag} />
            <main>
              <GraphErrorBoundary>
                <RightSide plateWell={state} />
              </GraphErrorBoundary>
            </main>
          </div>
        </span>
      </div>
    );
  }

  return (
    <div className="oneToolContainer flex flex-col" style={{ height: 'calc(100dvh - 112px)' }}>
      {header}
      <div style={{ flex: 1, minHeight: 0, paddingBottom: '1rem' }}>
        {rows.length > 0 ? grid : <span>No data yet..</span>}
      </div>
    </div>
  );
};

export default Details;
